import { NumericOptionDropdown } from 'components/dropdown/NumericOptionDropdown';
import { RadioButtonGroup } from 'components/forms/RadioButtonGroup';
import { getDisabledUpdateIntervalOptions } from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/collectionDurationValidation';
import { V4_SETTINGS_TEXT } from 'components/node_settings/v4SettingsTexts';
import {
    InfoPopupBullets,
    InlineDropdownWrapper,
    StackedSettingRow,
} from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/styled';
import { TriggeredSettings } from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/TriggeredSettings';
import {
    SettingName,
    StackedSettingValue,
} from 'components/node_settings/V4Settings/styled';
import { useFeatureFlag } from 'contexts/configcat/useFeatureFlag';
import {
    DEFAULT_TIMED,
    DEFAULT_TRIGGERED,
    UPDATE_INTERVAL_MODE_LABELS,
    UpdateInterval,
    UpdateIntervalMode,
    V4SensorSettings,
} from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import { useMemo, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { InfoPopup } from 'shared/info_popup/InfoPopup';
import { SmallSecondaryText } from 'styles/theme_styles/text';
import { OptionChild } from 'utils/node';

const UPDATE_INTERVAL_OPTIONS: { value: UpdateIntervalMode; label: string }[] = (
    ['smartsample', 'triggered', 'timed'] as const
).map((value) => ({ value, label: UPDATE_INTERVAL_MODE_LABELS[value] }));

const DEFAULT_INTERVAL_BY_MODE: Record<UpdateIntervalMode, UpdateInterval> = {
    smartsample: { mode: 'smartsample' },
    triggered: DEFAULT_TRIGGERED,
    timed: DEFAULT_TIMED,
};

export function UpdateIntervalHelperText({ mode }: { mode: UpdateIntervalMode }) {
    if (mode === 'smartsample') {
        return (
            <SmallSecondaryText>
                {V4_SETTINGS_TEXT.updateInterval.smartsampleHelper}{' '}
                <InfoPopup
                    title="SMARTsample"
                    triggerStyle={{
                        display: 'inline-flex',
                        verticalAlign: 'middle',
                        fontSize: '1.4rem',
                    }}
                >
                    <InfoPopupBullets>
                        <li>{V4_SETTINGS_TEXT.updateInterval.smartsampleHelper}</li>
                        <li>
                            {V4_SETTINGS_TEXT.updateInterval.smartsampleBulletBattery}
                        </li>
                        <li>
                            {V4_SETTINGS_TEXT.updateInterval.smartsampleBulletBaseline}
                        </li>
                    </InfoPopupBullets>
                </InfoPopup>
            </SmallSecondaryText>
        );
    }
    if (mode === 'triggered') {
        return (
            <SmallSecondaryText>
                {V4_SETTINGS_TEXT.updateInterval.triggeredHelperLine1}
                <br />
                {V4_SETTINGS_TEXT.updateInterval.triggeredHelperLine2}
            </SmallSecondaryText>
        );
    }
    return (
        <SmallSecondaryText>
            {V4_SETTINGS_TEXT.updateInterval.timedHelper}
        </SmallSecondaryText>
    );
}

type UpdateIntervalSettingProps = Readonly<{
    updateIntervalOptions: OptionChild<number>[];
    allAxesDisabled: boolean;
}>;

export function UpdateIntervalSetting({
    updateIntervalOptions,
    allAxesDisabled,
}: UpdateIntervalSettingProps) {
    const { control, setValue } = useFormContext<V4SensorSettings>();
    const updateInterval = useWatch({ control, name: 'updateInterval' });
    const [samplingFrequencyHz, numberOfSamples] = useWatch({
        control,
        name: ['samplingFrequencyHz', 'numberOfSamples'],
    });

    const disabledUpdateIntervalOptions = getDisabledUpdateIntervalOptions(
        updateIntervalOptions,
        { samplingFrequencyHz, numberOfSamples },
    );

    const { value: smartSampleEnabled } = useFeatureFlag('v4SensorsSmartSample');
    const { value: triggeredEnabled } = useFeatureFlag('v4SensorsTriggered');

    const updateIntervalDrafts = useRef<
        Partial<Record<UpdateIntervalMode, UpdateInterval>>
    >({});

    // Keep the saved mode selectable even when its flag is off, so Save round-trips the
    // real value instead of silently rewriting it.
    const updateIntervalModes = useMemo(
        () =>
            UPDATE_INTERVAL_OPTIONS.filter(
                (option) =>
                    option.value === 'timed' ||
                    (!allAxesDisabled &&
                        (option.value === updateInterval.mode ||
                            (option.value === 'smartsample' && smartSampleEnabled) ||
                            (option.value === 'triggered' && triggeredEnabled))),
            ),
        [allAxesDisabled, smartSampleEnabled, triggeredEnabled, updateInterval.mode],
    );

    return (
        <StackedSettingRow>
            <SettingName>{V4_SETTINGS_TEXT.settingNames.updateInterval}</SettingName>
            <StackedSettingValue>
                <RadioButtonGroup<UpdateIntervalMode>
                    radioStyle="indicator"
                    layout="horizontal"
                    name="updateIntervalMode"
                    value={updateInterval.mode}
                    items={updateIntervalModes}
                    onChange={(mode) => {
                        updateIntervalDrafts.current[updateInterval.mode] = {
                            ...updateInterval,
                        };
                        const next =
                            updateIntervalDrafts.current[mode] ??
                            DEFAULT_INTERVAL_BY_MODE[mode];
                        setValue('updateInterval', next, {
                            shouldDirty: true,
                            shouldValidate: true,
                        });
                    }}
                />
                <UpdateIntervalHelperText mode={updateInterval.mode} />
                {updateInterval.mode === 'timed' && (
                    <InlineDropdownWrapper>
                        <Controller
                            control={control}
                            name="updateInterval.intervalMs"
                            render={({ field }) => (
                                <NumericOptionDropdown
                                    value={field.value}
                                    options={updateIntervalOptions}
                                    onChange={field.onChange}
                                    disabledOptions={disabledUpdateIntervalOptions}
                                />
                            )}
                        />
                    </InlineDropdownWrapper>
                )}
                {updateInterval.mode === 'triggered' && (
                    <TriggeredSettings updateIntervalOptions={updateIntervalOptions} />
                )}
            </StackedSettingValue>
        </StackedSettingRow>
    );
}
